import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
interface AuthGuardProps {
  children: React.ReactNode;
  requiredUserType?: 'teacher' | 'student' | null;
  redirectTo?: string;
}
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredUserType = null,
  redirectTo = '/teacher-login'
}) => {
  const {
    currentUser,
    userType,
    loading
  } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // Only check after the auth state has been determined (not loading)
    if (!loading) {
      // If no user is logged in, redirect to login
      if (!currentUser) {
        navigate(redirectTo);
        return;
      }
      // CRITICAL: Check email verification for teachers
      // Unverified teachers must be redirected to verification page
      if (requiredUserType === 'teacher' && !currentUser.emailVerified) {
        console.log('[AuthGuard] Teacher email not verified, redirecting to verification page');
        navigate('/teacher-email-verification', {
          replace: true
        });
        return;
      }
      // If a specific user type is required and doesn't match
      if (requiredUserType && userType !== requiredUserType) {
        // Redirect based on the current user type
        if (userType === 'teacher') {
          navigate('/teacher-dashboard');
        } else if (userType === 'student') {
          navigate('/student-dashboard');
        } else {
          navigate(redirectTo);
        }
      }
    }
  }, [currentUser, userType, loading, navigate, redirectTo, requiredUserType]);
  // Show loading spinner while checking auth state or if verification fails
  if (loading || !currentUser || requiredUserType === 'teacher' && !currentUser.emailVerified || requiredUserType && userType !== requiredUserType) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-581">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500" data-id="element-582"></div>
      </div>;
  }
  // Render children if authenticated and verified (for teachers)
  return <>{children}</>;
};